import React, { useState } from 'react';
import {
    Image,
    FileText,
    Download,
    Trash2,
    Eye,
    X,
    ZoomIn,
    ZoomOut,
    RotateCw,
    ExternalLink,
    Calendar,
    User,
    File
} from 'lucide-react';

interface MediaFile {
    id?: string;
    url: string;
    filename: string;
    size: number;
    mimetype: string;
    type?: string;
    uploadedAt?: string;
    uploadedBy?: string;
}

interface MediaGalleryProps {
    files: MediaFile[];
    onDelete?: (file: MediaFile) => void;
    onDownload?: (file: MediaFile) => void;
    showActions?: boolean;
    showDetails?: boolean;
    layout?: 'grid' | 'list';
    maxHeight?: string;
    className?: string;
}

interface ImageViewerProps {
    image: MediaFile;
    onClose: () => void;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ image, onClose }) => {
    const [scale, setScale] = useState(1);
    const [rotation, setRotation] = useState(0);

    const handleZoom = (direction: 'in' | 'out') => {
        setScale(prev => direction === 'in' ? Math.min(prev * 1.2, 5) : Math.max(prev / 1.2, 0.2));
    };

    const handleRotate = () => {
        setRotation(prev => (prev + 90) % 360);
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = image.url;
        link.download = image.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
            {/* Controls */}
            <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10">
                <div className="flex items-center space-x-2 bg-black bg-opacity-50 rounded-lg px-3 py-2">
                    <button
                        onClick={() => handleZoom('out')}
                        className="text-white hover:text-gray-300 p-1"
                        title="Zoom Out"
                    >
                        <ZoomOut className="w-5 h-5" />
                    </button>
                    <span className="text-white text-sm min-w-[60px] text-center">
                        {Math.round(scale * 100)}%
                    </span>
                    <button
                        onClick={() => handleZoom('in')}
                        className="text-white hover:text-gray-300 p-1"
                        title="Zoom In"
                    >
                        <ZoomIn className="w-5 h-5" />
                    </button>
                    <div className="w-px h-6 bg-gray-600 mx-2" />
                    <button
                        onClick={handleRotate}
                        className="text-white hover:text-gray-300 p-1"
                        title="Rotate"
                    >
                        <RotateCw className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex items-center space-x-2">
                    <button
                        onClick={handleDownload}
                        className="text-white hover:text-gray-300 p-2 bg-black bg-opacity-50 rounded-lg"
                        title="Download"
                    >
                        <Download className="w-5 h-5" />
                    </button>
                    <button
                        onClick={onClose}
                        className="text-white hover:text-gray-300 p-2 bg-black bg-opacity-50 rounded-lg"
                        title="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Image */}
            <div className="flex items-center justify-center w-full h-full p-16">
                <img
                    src={image.url}
                    alt={image.filename}
                    className="max-w-full max-h-full object-contain transition-transform duration-200"
                    style={{
                        transform: `scale(${scale}) rotate(${rotation}deg)`
                    }}
                />
            </div>

            {/* Image Info */}
            <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-50 rounded-lg p-4 text-white">
                <h3 className="font-medium truncate">{image.filename}</h3>
                <div className="flex items-center space-x-4 text-sm text-gray-300 mt-1">
                    <span>{formatFileSize(image.size)}</span>
                    <span>{image.mimetype}</span>
                    {image.uploadedAt && (
                        <span>{new Date(image.uploadedAt).toLocaleDateString()}</span>
                    )}
                </div>
            </div>
        </div>
    );
};

const MediaGallery: React.FC<MediaGalleryProps> = ({
    files,
    onDelete,
    onDownload,
    showActions = true,
    showDetails = true,
    layout = 'grid',
    maxHeight = '400px',
    className = ''
}) => {
    const [selectedImage, setSelectedImage] = useState<MediaFile | null>(null);

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileIcon = (file: MediaFile) => {
        if (file.mimetype.startsWith('image/')) {
            return <Image className="w-6 h-6" />;
        } else if (file.mimetype === 'application/pdf') {
            return <FileText className="w-6 h-6 text-red-500" />;
        } else if (file.mimetype.includes('document') || file.mimetype.includes('word')) {
            return <FileText className="w-6 h-6 text-blue-500" />;
        }
        return <File className="w-6 h-6" />;
    };

    const handleFileClick = (file: MediaFile) => {
        if (file.mimetype.startsWith('image/')) {
            setSelectedImage(file);
        } else {
            // For non-images, open in new tab or download
            window.open(file.url, '_blank');
        }
    };

    const handleDownload = (file: MediaFile, e: React.MouseEvent) => {
        e.stopPropagation();
        if (onDownload) {
            onDownload(file);
        } else {
            const link = document.createElement('a');
            link.href = file.url;
            link.download = file.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleDelete = (file: MediaFile, e: React.MouseEvent) => {
        e.stopPropagation();
        if (onDelete && window.confirm('Are you sure you want to delete this file?')) {
            onDelete(file);
        }
    };

    if (files.length === 0) {
        return (
            <div className={`text-center py-8 text-gray-500 ${className}`}>
                <Image className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>No media files uploaded yet</p>
            </div>
        );
    }

    return (
        <>
            <div className={`${className}`}>
                {layout === 'grid' ? (
                    <div
                        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 overflow-y-auto"
                        style={{ maxHeight }}
                    >
                        {files.map((file, index) => (
                            <div
                                key={file.id || index}
                                className="group relative bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                                onClick={() => handleFileClick(file)}
                            >
                                {/* File Preview */}
                                <div className="aspect-square bg-gray-100 flex items-center justify-center">
                                    {file.mimetype.startsWith('image/') ? (
                                        <img
                                            src={file.url}
                                            alt={file.filename}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="flex flex-col items-center space-y-2">
                                            {getFileIcon(file)}
                                            <span className="text-xs text-gray-500 text-center px-2 truncate w-full">
                                                {file.filename.split('.').pop()?.toUpperCase()}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Overlay on hover */}
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center space-x-2">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleFileClick(file);
                                            }}
                                            className="p-2 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-colors"
                                            title="View"
                                        >
                                            {file.mimetype.startsWith('image/') ? (
                                                <Eye className="w-4 h-4 text-gray-700" />
                                            ) : (
                                                <ExternalLink className="w-4 h-4 text-gray-700" />
                                            )}
                                        </button>

                                        {showActions && (
                                            <>
                                                <button
                                                    onClick={(e) => handleDownload(file, e)}
                                                    className="p-2 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-colors"
                                                    title="Download"
                                                >
                                                    <Download className="w-4 h-4 text-gray-700" />
                                                </button>

                                                {onDelete && (
                                                    <button
                                                        onClick={(e) => handleDelete(file, e)}
                                                        className="p-2 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4 text-red-600" />
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* File Info */}
                                {showDetails && (
                                    <div className="p-2 bg-white">
                                        <p className="text-xs font-medium text-gray-900 truncate" title={file.filename}>
                                            {file.filename}
                                        </p>
                                        <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                                            <span>{formatFileSize(file.size)}</span>
                                            {file.uploadedAt && (
                                                <span>{new Date(file.uploadedAt).toLocaleDateString()}</span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div
                        className="space-y-2 overflow-y-auto"
                        style={{ maxHeight }}
                    >
                        {files.map((file, index) => (
                            <div
                                key={file.id || index}
                                className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow cursor-pointer"
                                onClick={() => handleFileClick(file)}
                            >
                                {/* File Icon/Thumbnail */}
                                <div className="flex-shrink-0">
                                    {file.mimetype.startsWith('image/') ? (
                                        <img
                                            src={file.url}
                                            alt={file.filename}
                                            className="w-12 h-12 object-cover rounded"
                                        />
                                    ) : (
                                        <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                                            {getFileIcon(file)}
                                        </div>
                                    )}
                                </div>

                                {/* File Info */}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                        {file.filename}
                                    </p>
                                    <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                                        <span>{formatFileSize(file.size)}</span>
                                        <span>{file.mimetype}</span>
                                        {file.uploadedAt && (
                                            <div className="flex items-center space-x-1">
                                                <Calendar className="w-3 h-3" />
                                                <span>{new Date(file.uploadedAt).toLocaleDateString()}</span>
                                            </div>
                                        )}
                                        {file.uploadedBy && (
                                            <div className="flex items-center space-x-1">
                                                <User className="w-3 h-3" />
                                                <span>{file.uploadedBy}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Actions */}
                                {showActions && (
                                    <div className="flex items-center space-x-1">
                                        <button
                                            onClick={(e) => handleDownload(file, e)}
                                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                            title="Download"
                                        >
                                            <Download className="w-4 h-4" />
                                        </button>

                                        {onDelete && (
                                            <button
                                                onClick={(e) => handleDelete(file, e)}
                                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Image Viewer Modal */}
            {selectedImage && (
                <ImageViewer
                    image={selectedImage}
                    onClose={() => setSelectedImage(null)}
                />
            )}
        </>
    );
};

// Export the formatFileSize function for use in other components
export const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default MediaGallery;